import {
  calendarReconcileColumns,
  type CalendarReconcileSubmitInput,
  type CalendarSingleView,
  createCalendarEventRange,
  parseCalendarAttendees,
  readCalendarEvent,
  resolveCalendarReconcileContext,
  toCalendarReconcileSnapshot,
  writeCalendarCellValue,
} from '@blocksuite/affine/data-view';
import { normalizeDatabaseShardManifest } from '@streamlain/database-identity';
import { Service } from '@toeverything/infra';

import type { DefaultServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import {
  CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
  DatabaseViewLocalDiscoveryService,
  type LocalDatabaseView,
  viewBackedAppResourceId,
  viewBackedAppSourceRefKey,
  viewBackedAreaScope,
} from '../view-backed-app';
import { isCalendarSourceEventEligible } from './calendar-source-eligibility';
import type {
  CalendarEventColumnMap,
  CalendarEventsResponse,
  CalendarMode,
  CalendarProjectedEvent,
  CalendarSource,
  CalendarSourceCandidate,
  CalendarSourcesResponse,
} from './types';
import { toCalendarAreaRef } from './types';

const CALENDAR_SOURCE_COLORS = [
  '#2f80ed',
  '#27ae60',
  '#f2994a',
  '#eb5757',
  '#9b51e0',
  '#00a3a3',
] as const;

interface LocalCalendarView extends LocalDatabaseView {
  columns: CalendarEventColumnMap;
  view: CalendarSingleView | null;
}

export interface CalendarLocalSourceInput {
  pendingAreaByDocId?: ReadonlyMap<string, string>;
  serverSources?: readonly CalendarSource[];
}

export interface CalendarLocalEventCreateInput {
  allDay?: boolean;
  day: Date;
  endMinute: number;
  startMinute: number;
}

export interface CalendarLocalEventCreateResult {
  reconcile: CalendarReconcileSubmitInput;
  rowId: string;
  startAt: string;
}

export interface CalendarLocalEventUpdateInput {
  allDay?: boolean;
  endAt: Date;
  rowId: string;
  startAt: Date;
}

interface CalendarLocalAccount {
  avatar?: string | null;
  id: string;
  info?: { avatarUrl?: string | null; name?: string | null } | null;
}

export function resolveLocalCalendarPresentation(input: {
  account: CalendarLocalAccount | null;
  area: CalendarSource['area'];
}): Pick<CalendarSource, 'owner' | 'presentationError'> {
  if (!input.area || input.area.kind !== 'private') {
    return { owner: null, presentationError: null };
  }
  const accountId = input.account?.id.trim();
  const ownerUserId = input.area.ownerUserId?.trim();
  const displayName = input.account?.info?.name?.trim();
  if (!accountId || !ownerUserId || accountId !== ownerUserId || !displayName) {
    return {
      owner: null,
      presentationError: 'owner_profile_unavailable',
    };
  }
  return {
    owner: {
      avatarUrl:
        input.account?.info?.avatarUrl?.trim() ||
        input.account?.avatar?.trim() ||
        null,
      displayName,
      userId: accountId,
    },
    presentationError: null,
  };
}

export class CalendarLocalProjectionService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly discovery: DatabaseViewLocalDiscoveryService,
    private readonly defaultServerService: DefaultServerService
  ) {
    super();
  }

  async listLocalSources(
    input: CalendarLocalSourceInput = {}
  ): Promise<CalendarSourcesResponse> {
    const account = this.defaultServerService.server.account$.value;
    const serverSourcesByKey = new Map(
      (input.serverSources ?? []).map(source => [
        viewBackedAppSourceRefKey(source),
        source,
      ])
    );
    const sources: CalendarSource[] = [];
    const views = await this.discovery.listLocalViews({
      descriptor: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      pendingAreaByDocId: input.pendingAreaByDocId,
    });

    for (const rawView of views) {
      const view = this.toCalendarView(rawView);
      const key = viewBackedAppSourceRefKey({
        blockId: view.blockId,
        databaseId: view.databaseId,
        docId: view.docId,
        viewId: view.viewId,
      });
      const serverSource = serverSourcesByKey.get(key);
      if (serverSource) {
        const localProjectionError = view.projectionError ?? null;
        const projectionError =
          localProjectionError ??
          (serverSource.projectionError === 'area-unavailable' &&
          serverSource.area
            ? null
            : serverSource.projectionError);
        sources.push({
          ...serverSource,
          columns: view.columns,
          projectionError,
          syncState: serverSource.syncState ?? 'synced',
        });
        continue;
      }
      sources.push(
        this.toLocalSource({
          account,
          docId: view.docId,
          index: sources.length,
          projectionError: view.area ? null : 'area-unavailable',
          view,
        })
      );
    }

    return {
      sources: sources.sort(compareSources),
    };
  }

  async listLocalCandidates(
    input: CalendarLocalSourceInput = {}
  ): Promise<{ candidates: CalendarSourceCandidate[] }> {
    const { sources } = await this.listLocalSources(input);
    return {
      candidates: sources.map(source => ({
        appKind: source.appKind,
        area: source.area,
        blockId: source.blockId,
        databaseId: source.databaseId,
        docId: source.docId,
        documentTitle:
          this.docsService.list.doc$(source.docId).value?.title$.value ?? null,
        projectionError: source.projectionError ?? null,
        title: source.title,
        viewId: source.viewId,
        viewMode: source.viewMode,
      })),
    };
  }

  async projectLocalEvents(input: {
    mode: CalendarMode;
    sources: readonly CalendarSource[];
    windowEndAt: string;
    windowStartAt: string;
  }): Promise<CalendarEventsResponse> {
    const start = parseWindowDate(input.windowStartAt, 'windowStartAt');
    const end = parseWindowDate(input.windowEndAt, 'windowEndAt');
    const events: CalendarProjectedEvent[] = [];
    const projectedSources: CalendarEventsResponse['sources'] = [];

    for (const source of input.sources) {
      if (!source.visible || source.lifecycleState !== 'active') {
        projectedSources.push(
          projectedSource(
            source,
            source.visible ? source.lifecycleState : 'hidden'
          )
        );
        continue;
      }
      if (!isCalendarSourceEventEligible(source)) {
        projectedSources.push(
          projectedSource(
            source,
            source.projectionError ?? source.presentationError!
          )
        );
        continue;
      }

      const opened = await this.discovery.openDoc(source.docId).catch(error => {
        console.warn('[CalendarLocalProjection] Failed to open source doc', {
          docId: source.docId,
          error,
        });
        return null;
      });
      if (!opened) {
        projectedSources.push(projectedSource(source, 'doc-unavailable'));
        continue;
      }
      try {
        try {
          const localView = await this.findCalendarView(
            opened.store,
            source.databaseId,
            source.viewId
          );
          if (!localView) {
            projectedSources.push(projectedSource(source, 'view-unavailable'));
            continue;
          }
          if (!localView.view) {
            projectedSources.push(
              projectedSource(
                source,
                localView.projectionError ?? 'projection-failed'
              )
            );
            continue;
          }
          const sourceEvents = this.projectSourceEvents({
            end,
            source,
            start,
            view: localView.view,
          });
          events.push(...sourceEvents);
          projectedSources.push(projectedSource(source, null));
        } catch (error) {
          console.warn(
            '[CalendarLocalProjection] Failed to project source events',
            {
              error,
              sourceId: source.id,
            }
          );
          projectedSources.push(projectedSource(source, 'projection-failed'));
        }
      } finally {
        opened.release();
      }
    }

    return {
      calendarEventContractVersion: 2,
      events: events.sort((a, b) => a.startAt.localeCompare(b.startAt)),
      sources: projectedSources,
      window: {
        endAt: input.windowEndAt,
        mode: input.mode,
        startAt: input.windowStartAt,
      },
    };
  }

  async createLocalEvent(
    source: CalendarSource,
    input: CalendarLocalEventCreateInput
  ): Promise<CalendarLocalEventCreateResult> {
    const opened = await this.discovery.openDoc(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Selected calendar view is not available locally.');
      }
      if (!source.canEdit || localView.view.readonly$.value) {
        throw new Error('Selected calendar is read-only.');
      }

      const created = createCalendarEventRange(
        localView.view,
        input.day,
        input.startMinute,
        input.endMinute,
        { allDay: input.allDay }
      );
      if (!created) throw new Error('Calendar event could not be created.');

      const context = resolveCalendarReconcileContext(
        localView.view.manager.dataSource
      );
      const columns = calendarReconcileColumns(localView.view);
      if (!context || !columns) {
        localView.view.rowsDelete([created.rowId]);
        throw new Error('Calendar reconcile context is unavailable.');
      }

      return {
        reconcile: {
          columns,
          context,
          previousEvent: null,
          rowId: created.rowId,
          skipMail: true,
          viewId: localView.view.id,
        },
        rowId: created.rowId,
        startAt: created.startAt.toISOString(),
      };
    } finally {
      opened.release();
    }
  }

  async updateLocalEvent(
    source: CalendarSource,
    input: CalendarLocalEventUpdateInput
  ): Promise<CalendarLocalEventCreateResult> {
    const opened = await this.discovery.openDoc(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Selected calendar view is not available locally.');
      }
      if (!source.canEdit || localView.view.readonly$.value) {
        throw new Error('Selected calendar is read-only.');
      }
      const previousEvent = readCalendarEvent(localView.view, input.rowId, {
        includeDetails: true,
      });
      if (!previousEvent) {
        throw new Error('Calendar event is not available locally.');
      }
      const context = resolveCalendarReconcileContext(
        localView.view.manager.dataSource
      );
      const columns = calendarReconcileColumns(localView.view);
      if (!context || !columns) {
        throw new Error('Calendar reconcile context is unavailable.');
      }

      writeCalendarCellValue(
        localView.view,
        input.rowId,
        localView.view.startDateCol$.value,
        input.startAt.getTime()
      );
      writeCalendarCellValue(
        localView.view,
        input.rowId,
        localView.view.endDateCol$.value,
        input.endAt.getTime()
      );
      if (input.allDay !== undefined) {
        writeCalendarCellValue(
          localView.view,
          input.rowId,
          localView.view.allDayCol$.value,
          input.allDay
        );
      }

      return {
        reconcile: {
          columns,
          context,
          previousEvent: toCalendarReconcileSnapshot(
            previousEvent,
            context,
            localView.view.id
          ),
          rowId: input.rowId,
          skipMail: true,
          viewId: localView.view.id,
        },
        rowId: input.rowId,
        startAt: input.startAt.toISOString(),
      };
    } finally {
      opened.release();
    }
  }

  async deleteLocalEvent(
    source: CalendarSource,
    rowId: string
  ): Promise<CalendarLocalEventCreateResult> {
    const opened = await this.discovery.openDoc(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Selected calendar view is not available locally.');
      }
      if (!source.canEdit || localView.view.readonly$.value) {
        throw new Error('Selected calendar is read-only.');
      }
      const previousEvent = readCalendarEvent(localView.view, rowId, {
        includeDetails: true,
      });
      if (!previousEvent) {
        throw new Error('Calendar event is not available locally.');
      }
      const context = resolveCalendarReconcileContext(
        localView.view.manager.dataSource
      );
      const columns = calendarReconcileColumns(localView.view);
      if (!context || !columns) {
        throw new Error('Calendar reconcile context is unavailable.');
      }

      localView.view.rowsDelete([rowId]);
      return {
        reconcile: {
          columns,
          context,
          previousEvent: toCalendarReconcileSnapshot(
            previousEvent,
            context,
            localView.view.id
          ),
          rowId,
          skipMail: true,
          viewId: localView.view.id,
        },
        rowId,
        startAt: previousEvent.startAt.toISOString(),
      };
    } finally {
      opened.release();
    }
  }

  async observeEventChanges(
    sources: readonly CalendarSource[],
    onChange: () => void
  ): Promise<() => void> {
    const releases: Array<() => void> = [];
    const observedDocs = new Set<object>();

    const observeDoc = (doc: {
      spaceDoc?: {
        off: (event: 'update', callback: () => void) => void;
        on: (event: 'update', callback: () => void) => void;
      };
    }) => {
      if (!doc.spaceDoc || observedDocs.has(doc.spaceDoc)) return;
      observedDocs.add(doc.spaceDoc);
      doc.spaceDoc.on('update', onChange);
      releases.push(() => doc.spaceDoc?.off('update', onChange));
    };

    for (const source of sources) {
      const opened = await this.discovery
        .openDoc(source.docId)
        .catch(() => null);
      if (!opened) continue;
      releases.push(opened.release);
      observeDoc(opened.store);

      const view = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      ).catch(() => null);
      const storageModel = view?.dataSource?.storageModel;
      if (!storageModel) continue;

      const storageStore = storageModel.store;
      observeDoc(storageStore);
      const manifest = normalizeDatabaseShardManifest(
        storageModel.props.databaseShardManifest
      );
      for (const shard of manifest?.shards ?? []) {
        const shardDoc = storageStore.workspace.getDoc(shard.serverDocId);
        if (shardDoc) observeDoc(shardDoc);
      }
    }

    return () => {
      for (const release of releases.reverse()) release();
    };
  }

  async renameLocalSource(
    source: CalendarSource,
    title: string
  ): Promise<void> {
    const normalizedTitle = title.trim();
    if (!normalizedTitle) {
      throw new Error('Calendar title must not be empty.');
    }
    const opened = await this.discovery.openDoc(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Calendar view is not available locally.');
      }
      if (localView.view.readonly$.value) {
        throw new Error('Calendar view is read-only.');
      }
      localView.view.nameSet(normalizedTitle);
    } finally {
      opened.release();
    }
  }

  private async findCalendarView(
    store: Parameters<
      DatabaseViewLocalDiscoveryService['findLocalView']
    >[0]['store'],
    databaseId: string,
    viewId: string
  ): Promise<LocalCalendarView | null> {
    const view = await this.discovery.findLocalView({
      databaseId,
      descriptor: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      store,
      viewId,
    });
    return view ? this.toCalendarView(view) : null;
  }

  private toCalendarView(view: LocalDatabaseView): LocalCalendarView {
    if (view.view && (view.view as { type?: unknown }).type === 'calendar') {
      return {
        ...view,
        columns: (view.diagnostics ?? {}) as CalendarEventColumnMap,
        view: view.view as CalendarSingleView,
      };
    }
    return {
      ...view,
      columns: {},
      projectionError: view.projectionError ?? 'projection-failed',
      view: null,
    };
  }

  private toLocalSource(input: {
    account: CalendarLocalAccount | null;
    docId: string;
    index: number;
    projectionError: string | null;
    view: LocalCalendarView;
  }): CalendarSource {
    const id = viewBackedAppResourceId(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      input.docId,
      input.view.databaseId,
      input.view.viewId
    );
    const canEdit = input.view.view?.readonly$.value === false;
    const area = input.view.area ? toCalendarAreaRef(input.view.area) : null;
    const presentation = resolveLocalCalendarPresentation({
      account: input.account,
      area,
    });
    return {
      accessDocId: input.docId,
      appKind: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR.appKind,
      area,
      blockId: input.view.blockId,
      canEdit,
      canManage: canEdit,
      color:
        CALENDAR_SOURCE_COLORS[input.index % CALENDAR_SOURCE_COLORS.length],
      columns: input.view.columns,
      databaseId: input.view.databaseId,
      deletedAt: null,
      docId: input.docId,
      id,
      lifecycleState: 'active',
      owner: presentation.owner,
      presentationError: presentation.presentationError,
      projectionError: input.projectionError ?? input.view.projectionError,
      scope: viewBackedAreaScope(area),
      sourcePresentationContractVersion: 1,
      syncState: 'local',
      title: input.view.title,
      trashedAt: null,
      viewId: input.view.viewId,
      viewMode: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR.viewMode,
      visible: true,
    };
  }

  private projectSourceEvents(input: {
    end: Date;
    source: CalendarSource;
    start: Date;
    view: CalendarSingleView;
  }): CalendarProjectedEvent[] {
    const startDateCol = input.view.startDateCol$.value;
    if (!startDateCol) return [];
    type ProjectionQuery = Parameters<CalendarSingleView['projectRows']>[0];
    const predicates: NonNullable<ProjectionQuery['predicates']> = [
      {
        endPropertyId: input.view.endDateCol$.value,
        startPropertyId: startDateCol,
        type: 'temporal-overlap',
        windowEnd: input.end.getTime(),
        windowStart: input.start.getTime(),
      },
    ];
    const projection = input.view.projectRows({
      predicates,
      scope: 'temporal-window',
      viewId: input.view.id,
      viewMode: input.view.type,
    });
    return projection.rows
      .flatMap(row => {
        try {
          const event = readCalendarEvent(input.view, row.rowId, {
            includeDetails: true,
          });
          return event ? [event] : [];
        } catch (error) {
          console.warn(
            '[CalendarLocalProjection] Failed to read calendar event row',
            {
              error,
              rowId: row.rowId,
              sourceId: input.source.id,
            }
          );
          return [];
        }
      })
      .map(event => {
        const columns = input.source.columns;
        return {
          allDay: event.allDay,
          attendeeCount: parseCalendarAttendees(event.attendees).length,
          canEdit: input.source.canEdit,
          color: input.source.color,
          endAt: event.endAt.toISOString(),
          icsSequence: readOptionalCalendarNumber(
            input.view,
            event.rowId,
            columns?.icsSequenceCol
          ),
          icsUid: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.icsUidCol
          ),
          id: `${input.source.id}:${event.rowId}`,
          invitationMethod: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.invitationMethodCol
          ),
          invitationState: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.invitationStateCol
          ),
          lifecycleState: input.source.lifecycleState,
          physicalLocation: event.locationPhysical || null,
          rowId: event.rowId,
          sourceId: input.source.id,
          sourceMailAccountId: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceMailAccountIdCol
          ),
          sourceMailAttachmentBlobKey: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceMailAttachmentBlobKeyCol
          ),
          sourceMailMessageId: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceMailMessageIdCol
          ),
          sourceType: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceTypeCol
          ),
          startAt: event.startAt.toISOString(),
          status: event.status || null,
          timezone:
            event.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            'UTC',
          title: event.title,
          virtualLocation: event.locationVirtual || null,
        };
      });
  }
}

function readOptionalCalendarString(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined
): string | null {
  if (!propertyId) return null;
  const value = view.cellGetOrCreate(rowId, propertyId).stringValue$.value;
  return value || null;
}

function readOptionalCalendarNumber(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined
): number | null {
  if (!propertyId) return null;
  const value = view.cellGetOrCreate(rowId, propertyId).jsonValue$.value;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function projectedSource(source: CalendarSource, error: string | null) {
  return {
    canEdit: source.canEdit,
    canManage: source.canManage,
    color: source.color,
    error,
    id: source.id,
    lifecycleState: source.lifecycleState,
    title: source.title,
    visible: source.visible,
  };
}

function parseWindowDate(value: string, field: string): Date {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    throw new Error(`Invalid calendar ${field}: ${value}`);
  }
  return date;
}

function compareSources(a: CalendarSource, b: CalendarSource): number {
  return a.title.localeCompare(b.title) || a.id.localeCompare(b.id);
}
